import React from "react";
import { useTranslation } from "react-i18next";
import { MessageCircle, Phone, Mail, MapPin, Building2, Hash } from "lucide-react";
import { whatsappLink, WHATSAPP_NUMBER } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const PHONE_DIGITS = "+31 6 4244 9019";
const PHONE_HREF = "tel:+31642449019";
const EMAIL = "info@trustlineservices.nl";
const ADDRESS_LINES = ["Julianastraat 52B", "2405 CJ Alphen aan den Rijn", "Netherlands"];

export default function Contact() {
  const { t } = useTranslation();
  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-16 md:py-24">
      <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#5BA83F]">{t("contact_page.eyebrow")}</p>
      <h1 className="font-heading text-4xl md:text-5xl font-extrabold text-[#1B3A6B] mt-3 tracking-tight">{t("contact_page.title")}</h1>
      <p className="mt-5 text-lg text-slate-600 max-w-2xl leading-relaxed">{t("contact_page.intro")}</p>

      <div className="mt-10 grid md:grid-cols-2 gap-5">
        <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="bg-white rounded-xl p-6 ring-1 ring-slate-200 hover:shadow-md hover:ring-[#5BA83F]/40 transition" data-testid="contact-whatsapp">
          <MessageCircle className="w-6 h-6 text-[#5BA83F]" />
          <h3 className="mt-3 font-heading font-semibold">{t("contact_page.ws_title")}</h3>
          <p className="text-sm text-slate-600 mt-1">{t("contact_page.ws_subtitle")}</p>
          <p className="mt-2 text-[#1B3A6B] font-mono" data-testid="contact-whatsapp-number">{WHATSAPP_NUMBER}</p>
        </a>

        <a href={`mailto:${EMAIL}`} className="bg-white rounded-xl p-6 ring-1 ring-slate-200 hover:shadow-md hover:ring-[#5BA83F]/40 transition" data-testid="contact-email">
          <Mail className="w-6 h-6 text-[#5BA83F]" />
          <h3 className="mt-3 font-heading font-semibold">{t("contact_page.email_title")}</h3>
          <p className="text-sm text-slate-600 mt-1">{t("contact_page.email_subtitle")}</p>
          <p className="mt-2 text-[#1B3A6B] font-medium break-all" data-testid="contact-email-address">{EMAIL}</p>
        </a>

        <a href={PHONE_HREF} className="bg-white rounded-xl p-6 ring-1 ring-slate-200 hover:shadow-md hover:ring-[#5BA83F]/40 transition" data-testid="contact-phone">
          <Phone className="w-6 h-6 text-[#5BA83F]" />
          <h3 className="mt-3 font-heading font-semibold">{t("contact_page.phone_title")}</h3>
          <p className="text-sm text-slate-600 mt-1">{t("contact_page.phone_subtitle")}</p>
          <p className="mt-2 text-[#1B3A6B] font-mono" data-testid="contact-phone-number">{PHONE_DIGITS}</p>
        </a>

        <div className="bg-white rounded-xl p-6 ring-1 ring-slate-200" data-testid="contact-address">
          <MapPin className="w-6 h-6 text-[#5BA83F]" />
          <h3 className="mt-3 font-heading font-semibold">{t("contact_page.address_title")}</h3>
          <address className="mt-2 not-italic text-sm text-slate-700 leading-relaxed">
            <div className="font-semibold">Trustline Schoonmaken</div>
            {ADDRESS_LINES.map((l) => <div key={l}>{l}</div>)}
          </address>
        </div>
      </div>

      {/* Business Information block */}
      <div className="mt-8 bg-slate-50 rounded-xl p-6 ring-1 ring-slate-200" data-testid="contact-business-info">
        <h3 className="font-heading font-semibold text-[#1B3A6B] flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#5BA83F]" />
          {t("contact_page.business_title")}
        </h3>
        <dl className="mt-4 grid sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
          <div className="flex justify-between sm:block">
            <dt className="text-slate-500">{t("contact_page.business_legal_name")}</dt>
            <dd className="font-medium text-slate-800 sm:mt-0.5">Trustline Schoonmaken</dd>
          </div>
          <div className="flex justify-between sm:block">
            <dt className="text-slate-500">{t("contact_page.business_trading_as")}</dt>
            <dd className="font-medium text-slate-800 sm:mt-0.5">Trustline Services NL</dd>
          </div>
          <div className="flex justify-between sm:block">
            <dt className="text-slate-500 inline-flex items-center gap-1"><Hash className="w-3 h-3" /> KvK</dt>
            <dd className="font-medium text-slate-800 sm:mt-0.5">99642441</dd>
          </div>
          <div className="flex justify-between sm:block">
            <dt className="text-slate-500 inline-flex items-center gap-1"><Hash className="w-3 h-3" /> VAT</dt>
            <dd className="font-medium text-slate-800 sm:mt-0.5">NL004861783B47</dd>
          </div>
        </dl>
      </div>

      <div className="mt-6 bg-white rounded-xl p-5 ring-1 ring-slate-200" data-testid="contact-area">
        <h3 className="font-heading font-semibold text-[#1B3A6B] flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#5BA83F]" />
          {t("contact_page.area_title")}
        </h3>
        <p className="mt-2 text-sm text-slate-600">{t("contact_page.area_subtitle")}</p>
        <p className="mt-2 text-slate-700">Amsterdam · Rotterdam · Utrecht · The Hague · Eindhoven · Alphen aan den Rijn</p>
      </div>

      <div className="mt-10 flex flex-wrap gap-3">
        <Link to="/quote"><Button size="lg" className="bg-[#1B3A6B] hover:bg-[#152D54] text-white rounded-full px-7 h-12" data-testid="contact-quote-cta">{t("contact_page.cta")}</Button></Link>
        <a href={whatsappLink()} target="_blank" rel="noopener noreferrer">
          <Button size="lg" variant="outline" className="rounded-full px-7 h-12 border-[#5BA83F] text-[#3F7A1F] hover:bg-[#E8F5E0]" data-testid="contact-whatsapp-cta">
            <MessageCircle className="w-4 h-4 mr-2" /> WhatsApp
          </Button>
        </a>
      </div>
    </div>
  );
}
